import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";

const LandingPage = () => {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !loading) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0600]">
        <div className="spinner-ember"></div>
      </div>
    );
  }

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#0A0600] text-[#FDF8EE] relative overflow-hidden" data-testid="landing-page">
      {/* Glow Effects */}
      <div className="glow glow1"></div>
      <div className="glow glow2"></div>

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 h-[60px] flex items-center justify-between px-6 md:px-12 border-b border-[rgba(232,160,48,0.1)] bg-[rgba(10,6,0,0.9)] backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className="w-[9px] h-[9px] bg-[#E8A030] rotate-45 flex-shrink-0"></div>
          <span className="font-serif font-black text-xl text-[#FDF8EE]">Ryan</span>
          <span className="hidden md:block font-mono text-[8px] tracking-[2px] uppercase text-[#8C7A5A] border-l border-[rgba(232,160,48,0.2)] pl-3 ml-1">
            AI Conversation Memory
          </span>
        </div>
        <div className="hidden md:flex items-center gap-8">
          <button onClick={() => scrollTo("verticals")} className="font-mono text-[10px] tracking-[2px] uppercase text-[#8C7A5A] hover:text-[#E8A030] transition-colors bg-transparent border-none cursor-pointer">Who it's for</button>
          <button onClick={() => scrollTo("features")} className="font-mono text-[10px] tracking-[2px] uppercase text-[#8C7A5A] hover:text-[#E8A030] transition-colors bg-transparent border-none cursor-pointer">Features</button>
          <button onClick={() => scrollTo("how")} className="font-mono text-[10px] tracking-[2px] uppercase text-[#8C7A5A] hover:text-[#E8A030] transition-colors bg-transparent border-none cursor-pointer">How it works</button>
          <button onClick={() => scrollTo("pricing")} className="font-mono text-[10px] tracking-[2px] uppercase text-[#8C7A5A] hover:text-[#E8A030] transition-colors bg-transparent border-none cursor-pointer">Pricing</button>
        </div>
        <button onClick={login} className="btn-primary" data-testid="header-login-btn">
          Install Ryan →
        </button>
      </nav>

      {/* Hero */}
      <section className="relative z-10 min-h-screen grid lg:grid-cols-2 gap-8 lg:gap-16 px-6 md:px-12 pt-[140px] pb-20 items-center border-b border-[rgba(232,160,48,0.1)]">
        <div className="flex flex-col gap-7 animate-fade-up">
          <div className="eyebrow">Conversation memory · Built for knowledge workers</div>
          
          <h1 className="font-serif font-black text-[clamp(48px,7vw,96px)] leading-[0.88] tracking-[-2px]">
            <span className="block text-[#FDF8EE]">Ryan</span>
            <em className="text-[#E8A030] italic">remembers</em><br/>
            everything.
          </h1>
          
          <p className="text-[17px] leading-[1.85] text-[#8C7A5A] max-w-[460px]">
            The insight was in that AI conversation. The explanation that made it click.
            The spec that took three hours to get right. <strong className="text-[#FDF8EE] font-medium">It is gone now</strong> —
            buried in a scroll you cannot search, in a session that has no memory of what mattered.
            Ryan pins any moment in any AI conversation, annotates it in your own words,
            and surfaces it the moment you need it again.
          </p>
          
          <div className="flex gap-4 flex-wrap items-center">
            <button onClick={login} className="btn-primary" data-testid="hero-login-btn">
              Get Started Free →
            </button>
            <button onClick={() => scrollTo("how")} className="btn-ghost" data-testid="hero-how-btn">
              See how it works
            </button>
          </div>
          
          <div className="font-mono text-[9px] text-[#4A3A1A] tracking-[0.5px]">
            Chrome extension · Claude · ChatGPT · Gemini · Free to start
          </div>
        </div>

        {/* Product Mock */}
        <div className="hidden lg:block animate-fade-up" style={{ animationDelay: '0.12s' }}>
          <div className="flex flex-col gap-2">
            <div className="bg-[#1C1004] border border-[rgba(232,160,48,0.12)] overflow-hidden">
              <div className="bg-[#170e02] px-4 py-2 flex items-center gap-2 border-b border-[rgba(232,160,48,0.07)]">
                <div className="flex gap-[5px]">
                  <div className="w-[9px] h-[9px] rounded-full bg-[#FF5F57]"></div>
                  <div className="w-[9px] h-[9px] rounded-full bg-[#FFBD2E]"></div>
                  <div className="w-[9px] h-[9px] rounded-full bg-[#28C840]"></div>
                </div>
                <div className="flex-1 bg-[rgba(232,160,48,0.04)] border border-[rgba(232,160,48,0.07)] px-3 py-1 font-mono text-[9px] text-[#4A3A1A] text-center rounded-sm">
                  claude.ai / chat
                </div>
              </div>
              <div className="p-3 flex flex-col gap-2">
                <div className="group relative p-3 text-[11px] leading-[1.55] bg-[rgba(232,160,48,0.07)] border-l-2 border-[#E8A030] text-[#F5ECD0] ml-4">
                  Explain why the Krebs cycle produces NADH and not NADPH
                  <div className="pin-btn absolute right-[-8px] top-1/2 -translate-y-1/2">◈</div>
                </div>
                <div className="group relative p-3 text-[11px] leading-[1.55] bg-[rgba(28,16,4,0.5)] border-l-2 border-[rgba(74,58,26,0.4)] text-[#8C7A5A] mr-4">
                  <div className="absolute left-[-6px] top-2 w-1 h-1 bg-[#E8A030] rotate-45"></div>
                  <strong className="text-[#FDF8EE] font-medium">The key is the enzyme context.</strong> NADH is produced because the dehydrogenase enzymes in the Krebs cycle are NAD+-dependent...
                  <div className="pin-btn absolute right-[-8px] top-1/2 -translate-y-1/2">◈</div>
                </div>
              </div>
            </div>
            <div className="bg-[#1C1004] border border-[rgba(232,160,48,0.12)]">
              <div className="px-4 py-2 border-b border-[rgba(232,160,48,0.08)] flex items-center justify-between">
                <span className="font-serif text-sm font-bold text-[#E8A030]">Ryan</span>
                <span className="font-mono text-[7px] tracking-[2px] uppercase text-[#C8600A] border border-[rgba(200,96,10,0.3)] px-1.5 py-0.5">11 saved</span>
              </div>
              <div className="mx-3 my-2 bg-[rgba(232,160,48,0.04)] border border-[rgba(232,160,48,0.08)] px-3 py-1.5 font-mono text-[9px] text-[#4A3A1A] flex items-center gap-1.5">
                <span>⌕</span><span>Search all bookmarks...</span>
                <span className="inline-block w-[1.5px] h-[0.9em] bg-[#E8A030] ml-0.5 animate-pulse"></span>
              </div>
              <div className="border-b border-[rgba(232,160,48,0.06)]">
                <div className="collection-header">
                  <span className="font-mono text-[8px] tracking-[1.5px] uppercase text-[#7AB8A0]">◈ Biochem Midterm</span>
                  <span className="font-mono text-[7px] text-[#4A3A1A]">4 bookmarks</span>
                </div>
                <div className="bookmark-item">
                  <div className="text-[10px] text-[#F5ECD0] leading-[1.4] mb-0.5">NADH = fuel for ATP · NADPH = fuel for building</div>
                  <div className="font-mono text-[7px] text-[#4A3A1A]">Claude · Today</div>
                  <div className="mt-1 flex gap-1">
                    <span className="tag tag-stu">exam</span>
                    <span className="tag">review</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[rgba(232,160,48,0.06)]">
                <div className="collection-header">
                  <span className="font-mono text-[8px] tracking-[1.5px] uppercase text-[#E8A030]">◈ Investor pitch</span>
                  <span className="font-mono text-[7px] text-[#4A3A1A]">5 bookmarks</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="relative z-10 py-24 px-6 md:px-12 border-b border-[rgba(232,160,48,0.1)]" id="problem">
        <div className="max-w-[820px] mx-auto text-center">
          <div className="eyebrow justify-center mb-7">The problem Ryan solves</div>
          <blockquote className="font-serif text-[clamp(20px,3vw,38px)] font-bold italic text-[#FDF8EE] leading-[1.2] mb-6">
            "You had the breakthrough at 11pm.<br/>
            By morning you are scrolling 800 lines to find it.<br/>
            <em className="text-[#E8A030] not-italic">Sometimes you never do.</em>"
          </blockquote>
          <p className="text-[15px] leading-[1.85] text-[#8C7A5A] max-w-[680px] mx-auto">
            AI conversations are where serious work happens now — strategy sessions, tutoring breakthroughs, 
            research synthesis, product decisions. The thinking is extraordinary. The retrieval is impossible. 
            There is no bookmark. No highlight. No margin note. No way to say "this exact moment — come back here."
            Ryan fixes the one thing every AI tool forgot to build.
          </p>
        </div>
      </section>

      {/* Three Verticals */}
      <section className="relative z-10 grid md:grid-cols-3 gap-[1px] bg-[rgba(232,160,48,0.08)] border-b border-[rgba(232,160,48,0.1)]" id="verticals">
        {[
          { label: "Professionals", color: "#E8A030", icon: "◈", title: "The decision that took three hours to reach", body: "Founders, executives, lawyers, consultants — you work through strategy, pricing, investor narratives, and client deliverables inside long AI sessions. The output is extraordinary. Finding any specific piece of it tomorrow is not. Ryan pins every decision, every approved framing, every insight that matters — organized by project, searchable in plain text.", example: "The pricing principle we landed on at 11:47pm — pinned, tagged 'investor pitch,' marked approved. Found in 4 seconds the next morning." },
          { label: "Students", color: "#7AB8A0", icon: "◎", title: "The explanation that finally made it click", body: "You used Claude to work through a concept that your textbook never explained clearly. The exchange where it finally clicked is in that conversation — the exact wording, the example, the mnemonic you came up with. Ryan captures that moment and turns your AI tutoring sessions into a personal study system organized by course, exam, and concept.", example: "NADH vs NADPH — the enzyme context explanation that made sense — pinned to Biochem Midterm, tagged 'will be on exam'. Review mode surfaces it with my own note." },
          { label: "Researchers", color: "#A08CC8", icon: "⬡", title: "The argument that formed before the paper", body: "Scientists, PhD candidates, policy analysts, journalists — your AI conversations are the intellectual workbench where ideas form before they become publications. The synthesis that emerged in session four needs to be findable in session forty. Ryan is the lab notebook for AI-assisted thinking — organized by project, annotated with your own commentary, searchable across months of work.", example: "The methodological critique from the session two weeks ago — saved to Thesis Ch. 3, tagged 'verify source,' note added: ask supervisor. Still there six weeks later." }
        ].map((vert, i) => (
          <div key={i} className="bg-[#0A0600] p-10 md:p-14 relative overflow-hidden group hover:bg-[#211506] transition-colors" data-testid={`vertical-${vert.label.toLowerCase()}`}>
            <div className="absolute top-0 left-0 right-0 h-[3px] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" style={{ background: vert.color }}></div>
            <div className="text-[28px] mb-5">{vert.icon}</div>
            <div className="font-mono text-[8px] tracking-[3px] uppercase mb-3" style={{ color: vert.color }}>{vert.label}</div>
            <h3 className="font-serif text-[clamp(18px,2.2vw,26px)] font-bold text-[#FDF8EE] leading-[1.2] mb-4 tracking-[-0.3px]">{vert.title}</h3>
            <p className="text-[13px] text-[#8C7A5A] leading-[1.8] mb-5">{vert.body}</p>
            <div className="font-mono text-[10px] text-[#4A3A1A] border-t border-[rgba(232,160,48,0.07)] pt-4 leading-[1.7] italic">
              "{vert.example}"
            </div>
            <div className="absolute bottom-[-16px] right-[-8px] font-serif text-[100px] font-black italic leading-none pointer-events-none" style={{ color: vert.color, opacity: 0.025 }}>
              {vert.label.slice(0, 3)}
            </div>
          </div>
        ))}
      </section>

      {/* Lawyers Section */}
      <section className="relative z-10 border-b border-[rgba(232,160,48,0.1)]" id="lawyers" data-testid="lawyers-section">
        {/* Lawyers Banner */}
        <div className="bg-[#0A0600] p-10 md:p-14 border-b border-[rgba(232,160,48,0.08)]">
          <div className="font-mono text-[8px] tracking-[3px] uppercase mb-3 text-[#E8A030]">Lawyers</div>
          <h3 className="font-serif text-[clamp(18px,2.2vw,26px)] font-bold text-[#FDF8EE] leading-[1.2] tracking-[-0.3px]">The argument you worked out at midnight</h3>
        </div>

        {/* Lawyer Cards */}
        <div className="grid md:grid-cols-3 gap-[1px] bg-[rgba(232,160,48,0.08)]">
          {[
            { label: "Case Strategy", color: "#E8A030", icon: "⚖️", title: "The position approved at 11pm", body: "Pin the strategic framing your team landed on. Attach the supporting memo. Tag it by matter and status. Find it before the 8am client call without rebuilding the reasoning from scratch.", example: "The damages theory we landed on after three sessions — pinned, tagged approved, PDF attached. Ready for the partner review in one click." },
            { label: "Research & Discovery", color: "#7AB8A0", icon: "📋", title: "The precedent that surfaced at the right moment", body: "Save AI-surfaced research with status flags — pending review, verified, needs partner sign-off. Organize by matter. Build a human-in-the-loop annotation layer on top of every AI output before it goes anywhere.", example: "All research pinned to the Rivera matter — status: pending partner review. Shared with co-counsel in one link." },
            { label: "Matter Organization", color: "#A08CC8", icon: "🗂️", title: "Six matters. One place to find anything.", body: "Separate volumes per matter. Every saved moment — deposition prep, contract markups, brief structure — filed where it belongs. Query across all matters in plain language when you need something fast.", example: "Asked Ryan: what did we decide about the indemnification clause across all active matters? Three results. Fifteen seconds." }
          ].map((vert, i) => (
            <div key={i} className="bg-[#0A0600] p-10 md:p-14 relative overflow-hidden group hover:bg-[#211506] transition-colors" data-testid={`lawyer-card-${i}`}>
              <div className="absolute top-0 left-0 right-0 h-[3px] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" style={{ background: vert.color }}></div>
              <div className="text-[28px] mb-5">{vert.icon}</div>
              <div className="font-mono text-[8px] tracking-[3px] uppercase mb-3" style={{ color: vert.color }}>{vert.label}</div>
              <h3 className="font-serif text-[clamp(18px,2.2vw,26px)] font-bold text-[#FDF8EE] leading-[1.2] mb-4 tracking-[-0.3px]">{vert.title}</h3>
              <p className="text-[13px] text-[#8C7A5A] leading-[1.8] mb-5">{vert.body}</p>
              <div className="font-mono text-[10px] text-[#4A3A1A] border-t border-[rgba(232,160,48,0.07)] pt-4 leading-[1.7] italic">
                "{vert.example}"
              </div>
              <div className="absolute bottom-[-16px] right-[-8px] font-serif text-[100px] font-black italic leading-none pointer-events-none" style={{ color: vert.color, opacity: 0.025 }}>
                {vert.label.slice(0, 3)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Knowledge Seekers Section */}
      <section className="relative z-10 border-b border-[rgba(232,160,48,0.1)]" id="knowledge-seekers" data-testid="knowledge-seekers-section">
        <div className="bg-[#0A0600] p-10 md:p-14 border-b border-[rgba(232,160,48,0.08)]">
          <div className="font-mono text-[8px] tracking-[3px] uppercase mb-3 text-[#7AB8A0]">Knowledge Seekers</div>
          <h3 className="font-serif text-[clamp(18px,2.2vw,26px)] font-bold text-[#FDF8EE] leading-[1.2] tracking-[-0.3px]">The rabbit hole that actually led somewhere</h3>
        </div>

        <div className="grid md:grid-cols-3 gap-[1px] bg-[rgba(232,160,48,0.08)]">
          {[
            { label: "Deep Dives", color: "#E8A030", icon: "🔍", title: "The thread you followed for three hours", body: "You asked one question and ended up six conversations deep — across history, science, policy, or whatever pulled you in. The thing you found at the end of that trail was worth the journey. Ryan saves the moments that mattered so the next time someone asks 'where did you read that?' you have an answer.", example: "Started with a question about trade routes. Ended up understanding the entire Bretton Woods system. The three exchanges that connected it all — pinned and tagged. Ready to share." },
            { label: "Learning Trails", color: "#7AB8A0", icon: "🧭", title: "The explanation you will want again in six months", body: "You use AI the way other people use libraries — to understand things deeply, not just get answers. Each session builds on the last. Ryan turns scattered conversations into a personal knowledge base — organized by topic, searchable by concept, annotated with what you thought was important and why.", example: "Everything I learned about CRISPR across four sessions — saved by concept, tagged by complexity level. My own syllabus, built from curiosity." },
            { label: "Fact Finding", color: "#A08CC8", icon: "🎯", title: "The source that settled the argument", body: "You care about getting it right. When AI surfaces a compelling explanation, a surprising statistic, or a well-structured argument, you want to hold onto it — verify it, annotate it, come back to it. Ryan gives you a place to collect what you have found and separate what you know from what you still need to check.", example: "The AI breakdown of microplastics research — pinned, tagged 'needs verification,' note: cross-reference with the EPA study. Found it two weeks later when the topic came up again." }
          ].map((vert, i) => (
            <div key={i} className="bg-[#0A0600] p-10 md:p-14 relative overflow-hidden group hover:bg-[#211506] transition-colors" data-testid={`seeker-card-${i}`}>
              <div className="absolute top-0 left-0 right-0 h-[3px] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" style={{ background: vert.color }}></div>
              <div className="text-[28px] mb-5">{vert.icon}</div>
              <div className="font-mono text-[8px] tracking-[3px] uppercase mb-3" style={{ color: vert.color }}>{vert.label}</div>
              <h3 className="font-serif text-[clamp(18px,2.2vw,26px)] font-bold text-[#FDF8EE] leading-[1.2] mb-4 tracking-[-0.3px]">{vert.title}</h3>
              <p className="text-[13px] text-[#8C7A5A] leading-[1.8] mb-5">{vert.body}</p>
              <div className="font-mono text-[10px] text-[#4A3A1A] border-t border-[rgba(232,160,48,0.07)] pt-4 leading-[1.7] italic">
                "{vert.example}"
              </div>
              <div className="absolute bottom-[-16px] right-[-8px] font-serif text-[100px] font-black italic leading-none pointer-events-none" style={{ color: vert.color, opacity: 0.025 }}>
                {vert.label.slice(0, 3)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="relative z-10 py-24 px-6 md:px-12 border-b border-[rgba(232,160,48,0.1)]" id="features">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[300px_1fr] gap-16">
          <div className="lg:sticky lg:top-20 lg:self-start">
            <div className="font-mono text-[9px] tracking-[4px] uppercase text-[#C8600A] flex items-center gap-3 mb-4">
              <span className="w-5 h-[1px] bg-[#C8600A]"></span>
              What Ryan does
            </div>
            <h2 className="font-serif text-[clamp(28px,3.5vw,48px)] font-black leading-[1.02] tracking-[-1.5px] mb-4">
              Three things.<br/><em className="italic text-[#E8A030]">One memory.</em>
            </h2>
            <p className="text-[14px] text-[#8C7A5A] leading-[1.8]">
              Ryan layers conversation memory on top of every AI tool you already use. It does not replace Claude or ChatGPT. 
              It <strong className="text-[#FDF8EE] font-medium">remembers what they produce</strong>, the way you want it remembered.
            </p>
          </div>
          
          <div className="flex flex-col gap-[1px] bg-[rgba(232,160,48,0.08)]">
            {[
              { num: "01", name: "Bookmark", rest: "any moment", desc: "Hover any AI response and click the pin. Ryan saves the exact exchange — not the whole conversation, the specific moment. It captures the text, the timestamp, the conversation context, and a link back to the precise position in the thread. Works in Claude, ChatGPT, Gemini, and any web-based AI interface. The pin appears in one click and takes under five seconds." },
              { num: "02", name: "Annotate", rest: "in your own words", desc: "Attach your note to any bookmark immediately or later. Tag it by status — 'use in pitch,' 'will be on exam,' 'verify source,' 'approved,' 'revisit.' The annotation is yours, separate from what the AI said. When you surface the bookmark, you see both layers: what the AI produced and what you thought about it at the time." },
              { num: "03", name: "Volumes", rest: "across everything", desc: "Organize bookmarks from multiple conversations and multiple AI tools into named volumes. 'Biochem Midterm.' 'Investor pitch.' 'Thesis Chapter 3.' Search across all of them in plain text. Filter by tag, date, AI tool, or project. The intelligence from months of AI conversations — organized the way you think, not the way the tool stores it." }
            ].map((feat, i) => (
              <div key={i} className="bg-[#1C1004] p-8 relative overflow-hidden group hover:bg-[#211506] transition-colors grid grid-cols-[52px_1fr] gap-4 items-start cursor-pointer" data-testid={`feature-card-${feat.num}`}>
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#C8600A] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                <span className="font-mono text-[11px] text-[#4A3A1A] tracking-[1px] pt-1">{feat.num}</span>
                <div>
                  <h3 className="font-serif text-[22px] font-bold text-[#FDF8EE] mb-2 tracking-[-0.3px]">
                    <span className="text-[#E8A030] italic">{feat.name}</span> {feat.rest}
                  </h3>
                  <p className="text-[13px] text-[#8C7A5A] leading-[1.75]">{feat.desc}</p>
                </div>
                <span className="absolute bottom-[-14px] right-[-6px] font-serif text-[100px] font-black italic text-[rgba(232,160,48,0.024)] pointer-events-none">{i + 1}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative z-10 py-24 px-6 md:px-12 border-b border-[rgba(232,160,48,0.1)]" id="how">
        <div className="max-w-6xl mx-auto">
          <div className="eyebrow mb-5">How it works</div>
          <h2 className="font-serif text-[clamp(28px,3.5vw,48px)] font-black leading-[1.02] tracking-[-1.5px] mb-3 max-w-[560px]">
            Install once.<br/><em className="italic text-[#E8A030]">Remember everything.</em>
          </h2>
          <p className="text-[15px] text-[#8C7A5A] max-w-[500px] leading-[1.8] mb-14">
            Three steps from installation to never losing an insight again.
          </p>
          
          <div className="grid md:grid-cols-3 gap-[1px] bg-[rgba(232,160,48,0.08)]">
            {[
              { num: "1", icon: "⬡", title: "Install Ryan", desc: "Add Ryan to Chrome in 30 seconds. No account required to start bookmarking. Ryan immediately places a pin icon on every AI response in every supported tool — ready to use the moment you need it.", hasArrow: true },
              { num: "2", icon: "◈", title: "Pin what matters", desc: "Hover any response. Click the pin. Ryan saves the exact exchange with its full context. Add your note immediately or later. Tag it. Assign it to a volume. The whole workflow takes under 10 seconds.", hasArrow: true },
              { num: "3", icon: "◎", title: "Find it when you need it", desc: "Open the Ryan sidebar or dashboard. Search in plain text across everything you have saved — across tools, sessions, and weeks. What took 20 minutes of scrolling now takes 10 seconds.", hasArrow: false }
            ].map((step, i) => (
              <div key={i} className="bg-[#1C1004] p-8 md:p-10 relative overflow-hidden" data-testid={`how-step-${step.num}`}>
                <div className="font-serif text-[64px] font-black italic text-[rgba(232,160,48,0.07)] leading-none mb-3">{step.num}</div>
                <div className="text-[22px] mb-4">{step.icon}</div>
                <h3 className="font-serif text-[21px] font-bold text-[#FDF8EE] mb-3 tracking-[-0.3px]">{step.title}</h3>
                <p className="text-[13px] text-[#8C7A5A] leading-[1.75]">{step.desc}</p>
                {step.hasArrow && (
                  <div className="hidden md:flex absolute right-[-12px] top-1/2 -translate-y-1/2 bg-[#0A0600] w-[22px] h-[22px] items-center justify-center font-mono text-[11px] text-[#4A3A1A] z-10">→</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tailored Section */}
      <section className="relative z-10 py-24 px-6 md:px-12 border-b border-[rgba(232,160,48,0.1)]" id="tailored">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-[640px] mb-16">
            <div className="eyebrow mb-4">Built for how you actually work</div>
            <h2 className="font-serif text-[clamp(28px,3.5vw,48px)] font-black leading-[1.02] tracking-[-1.5px] mb-4">
              Same product.<br/><em className="italic text-[#E8A030]">Your context.</em>
            </h2>
            <p className="text-[14px] text-[#8C7A5A] leading-[1.8]">
              Ryan does not know if you are a founder or a PhD student. It just pins what you pin and organizes what you name. 
              But these are the patterns we designed for — the three kinds of work where AI conversation memory changes everything.
            </p>
          </div>

          <div className="flex flex-col gap-[1px] bg-[rgba(232,160,48,0.08)]">
            {[
              {
                color: "#E8A030", label: "Professionals", subtitle: "Founders · Executives · Lawyers · Consultants",
                problem: "Decisions, strategies, and deliverables emerge from long AI sessions. The output is extraordinary. Retrieving any specific piece of it the next day — to use in a deck, a memo, a client document — requires scrolling through hundreds of lines with no search, no structure, no memory of where anything was.",
                collections: [
                  "Investor pitch materials — approved framings and key principles",
                  "Client Q4 strategy — decisions by session, tagged for review",
                  "Legal research by matter — citable exchanges with your notes",
                  "Product spec history — four versions, all bookmarked, final tagged approved"
                ]
              },
              {
                color: "#7AB8A0", label: "Students", subtitle: "Undergraduates · Grad students · Professional certifications",
                problem: "AI tutoring produces the clearest explanations of concepts that textbooks never make accessible. The exchange where something finally clicked is in that session — the exact wording, the example, the mnemonic you wrote in the margins of your note. Next week, before the exam, you cannot find it. Ryan turns AI tutoring sessions into a personal study archive organized by course, exam, and concept.",
                collections: [
                  "Biochem Midterm — key concepts, tagged 'will be on exam'",
                  "Constitutional Law Final — case analysis, annotated with my own argument",
                  "CS 301 Data Structures — code explanations I can actually understand",
                  "Thesis research — sources to verify, questions for my advisor"
                ]
              },
              {
                color: "#A08CC8", label: "Researchers", subtitle: "Scientists · PhD candidates · Policy analysts · Journalists",
                problem: "Research happens over months. AI conversations are the intellectual workbench where hypotheses form, literature gets synthesized, and arguments get pressure-tested. The exchange that shaped the direction of the methodology section needs to be findable six months later when writing the discussion. Ryan is the lab notebook for AI-assisted thinking — annotated, organized, permanent.",
                collections: [
                  "Thesis Ch. 3 — methodology arguments, tagged 'verify before citing'",
                  "Literature review — synthesis sessions across 14 papers",
                  "Policy brief — key findings, annotated with my counterarguments",
                  "Interview prep — source questions, follow-up threads, verified facts"
                ]
              }
            ].map((row, i) => (
              <div key={i} className="bg-[#1C1004] grid lg:grid-cols-[280px_1fr_1fr] gap-0 border-b border-[rgba(232,160,48,0.06)] last:border-b-0 hover:bg-[#211506] transition-colors" data-testid={`tailored-${row.label.toLowerCase()}`}>
                <div className="p-8 lg:p-10 lg:border-r border-[rgba(232,160,48,0.08)]" style={{ borderTop: `3px solid ${row.color}` }}>
                  <div className="font-mono text-[8px] tracking-[3px] uppercase mb-3" style={{ color: row.color }}>{row.label}</div>
                  <div className="font-serif text-[20px] lg:text-[24px] font-bold text-[#FDF8EE] tracking-[-0.3px] leading-[1.1]">{row.subtitle}</div>
                </div>
                <div className="p-8 lg:p-10 lg:border-r border-[rgba(232,160,48,0.08)]">
                  <div className="font-mono text-[8px] tracking-[2px] uppercase text-[#4A3A1A] mb-3">The problem Ryan solves</div>
                  <p className="text-[13px] text-[#8C7A5A] leading-[1.75]">{row.problem}</p>
                </div>
                <div className="p-8 lg:p-10">
                  <div className="font-mono text-[8px] tracking-[2px] uppercase text-[#4A3A1A] mb-3">Volumes that matter</div>
                  <div className="flex flex-col gap-2">
                    {row.collections.map((c, j) => (
                      <div key={j} className="flex items-start gap-2 text-[12px] text-[#8C7A5A] leading-[1.5]">
                        <div className="w-1 h-1 rounded-full flex-shrink-0 mt-[7px]" style={{ background: row.color }}></div>
                        {c}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="relative z-10 py-20 px-6 md:px-12 border-b border-[rgba(232,160,48,0.1)] overflow-hidden" id="manifesto">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45 font-serif text-[210px] font-black italic text-[rgba(232,160,48,0.024)] whitespace-nowrap pointer-events-none tracking-[-8px]">
          memory
        </div>
        <div className="relative z-10 max-w-[760px] mx-auto text-center">
          <div className="w-[1px] h-12 bg-[rgba(232,160,48,0.18)] mx-auto mb-6"></div>
          <div className="font-serif text-[clamp(20px,3.2vw,38px)] font-bold text-[#FDF8EE] leading-[1.15] mb-5">
            Every AI conversation produces something worth keeping.
          </div>
          <div className="font-serif text-[clamp(20px,3.2vw,38px)] font-bold italic text-[#E8A030] leading-[1.15] mb-5">
            None of them have a way to keep it.
          </div>
          <div className="w-[1px] h-5 bg-[rgba(232,160,48,0.18)] mx-auto my-5"></div>
          <div className="font-serif text-[clamp(14px,2vw,22px)] text-[#8C7A5A] leading-[1.15] mb-3">
            Not the professional who built a strategy over four sessions.
          </div>
          <div className="font-serif text-[clamp(14px,2vw,22px)] text-[#8C7A5A] leading-[1.15] mb-3">
            Not the student whose breakthrough came at midnight.
          </div>
          <div className="font-serif text-[clamp(14px,2vw,22px)] text-[#8C7A5A] leading-[1.15] mb-5">
            Not the researcher whose hypothesis formed in conversation three.
          </div>
          <div className="w-[1px] h-5 bg-[rgba(232,160,48,0.18)] mx-auto my-5"></div>
          <div className="font-serif text-[clamp(18px,2.5vw,32px)] italic text-[#E8A030]">
            Ryan remembers.
          </div>
        </div>
      </section>

      {/* Info Strip */}
      <div className="relative z-10 py-9 px-6 md:px-12 border-b border-[rgba(232,160,48,0.1)] bg-[#1C1004] flex flex-col md:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-3.5">
          <div className="w-[10px] h-[10px] bg-[#E8A030] rotate-45 flex-shrink-0"></div>
          <span className="font-mono text-[9px] tracking-[2px] uppercase text-[#8C7A5A]">
            <strong className="text-[#E8A030]">Ryan</strong> · AI Conversation Memory · Chrome extension
          </span>
        </div>
        <span className="font-mono text-[9px] text-[#4A3A1A] tracking-[0.5px]">
          Available now · Free to start · Signal & Synthesis tiers
        </span>
      </div>

      {/* Pricing */}
      <section className="relative z-10 py-24 px-6 md:px-12 border-b border-[rgba(232,160,48,0.1)]" id="pricing">
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-12">
            <div className="eyebrow justify-center mb-4">Simple pricing</div>
            <h2 className="font-serif text-[clamp(28px,3.5vw,48px)] font-black leading-[1.02] tracking-[-1.5px] mb-4">
              Start free.<br/><em className="italic text-[#E8A030]">Scale when ready.</em>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {/* Free */}
            <div className="bg-[#1C1004] border border-[rgba(232,160,48,0.08)] p-8 rounded-sm" data-testid="free-plan-card">
              <div className="mb-5">
                <h3 className="font-serif text-xl font-bold text-[#FDF8EE] mb-1">Free</h3>
                <p className="font-mono text-[9px] text-[#A8987A] uppercase tracking-[1px]">Get started</p>
              </div>
              <div className="mb-5">
                <span className="font-serif text-4xl font-black text-[#FDF8EE]">$0</span>
                <span className="font-mono text-[10px] text-[#A8987A]">/month</span>
              </div>
              <ul className="space-y-2.5 mb-6">
                {["10 entries", "1 volume", "Annotations & attachments", "Dashboard + library view", "Chrome extension"].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-[12px] text-[#8C7A5A]">
                    <span className="text-[#7AB8A0]">✓</span> {f}
                  </li>
                ))}
              </ul>
              <button onClick={login} className="btn-ghost w-full" data-testid="free-plan-btn">Get Started</button>
            </div>

            {/* Signal */}
            <div className="bg-[#1C1004] border-2 border-[#E8A030] p-8 rounded-sm relative -mt-2 mb-2 md:-mt-3 md:mb-3" data-testid="signal-plan-card">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-[#E8A030] text-[#0A0600] font-mono text-[8px] font-bold tracking-[1px] uppercase px-3 py-1">Most Popular</span>
              </div>
              <div className="mb-5">
                <h3 className="font-serif text-xl font-bold text-[#FDF8EE] mb-1 flex items-center gap-2">Signal <span className="text-[#E8A030]">◈</span></h3>
                <p className="font-mono text-[9px] text-[#A8987A] uppercase tracking-[1px]">For individuals</p>
              </div>
              <div className="mb-1">
                <span className="font-serif text-4xl font-black text-[#FDF8EE]">$15</span>
                <span className="font-mono text-[10px] text-[#A8987A]">/month</span>
              </div>
              <div className="font-mono text-[9px] text-[#A8987A] mb-5">or $144/year</div>
              <ul className="space-y-2.5 mb-6">
                {["Unlimited entries", "Unlimited volumes", "50 Ask Ryan queries per month", "Image attachments + OCR", "Notion sync", "JSON & Markdown export", "Share volumes via link"].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-[12px] text-[#F5ECD0]">
                    <span className="text-[#E8A030]">✓</span> {f}
                  </li>
                ))}
              </ul>
              <button onClick={login} className="btn-primary w-full" data-testid="signal-plan-btn">Get Signal →</button>
            </div>

            {/* Synthesis */}
            <div className="bg-[#1C1004] border border-[rgba(232,160,48,0.08)] p-8 rounded-sm" data-testid="synthesis-plan-card">
              <div className="mb-5">
                <h3 className="font-serif text-xl font-bold text-[#FDF8EE] mb-1 flex items-center gap-2">Synthesis <span className="text-[#A08CC8]">⬡</span></h3>
                <p className="font-mono text-[9px] text-[#A8987A] uppercase tracking-[1px]">For teams</p>
              </div>
              <div className="mb-1">
                <span className="font-serif text-4xl font-black text-[#FDF8EE]">$24</span>
                <span className="font-mono text-[10px] text-[#A8987A]">/user/month</span>
              </div>
              <div className="font-mono text-[9px] text-[#A8987A] mb-5">or $230/year per user</div>
              <ul className="space-y-2.5 mb-6">
                {["Everything in Signal", "Shared volumes", "150 Ask Ryan queries per user per month, pooled", "Team knowledge base", "Admin dashboard + SAML SSO", "Collaborator sharing", "SLA & priority support"].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-[12px] text-[#8C7A5A]">
                    <span className="text-[#A08CC8]">✓</span> {f}
                  </li>
                ))}
              </ul>
              <button onClick={login} className="btn-ghost w-full" data-testid="synthesis-plan-btn">Get Synthesis →</button>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mt-10 font-mono text-[9px] text-[#A8987A] uppercase tracking-[1px]">
            <span>Secure payments via Stripe</span>
            <span>Cancel anytime</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 py-32 px-6 md:px-12 text-center overflow-hidden" id="cta">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45 w-[380px] h-[380px] border border-[rgba(232,160,48,0.055)] pointer-events-none"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45 w-[600px] h-[600px] border border-[rgba(232,160,48,0.055)] pointer-events-none"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45 w-[820px] h-[820px] border border-[rgba(232,160,48,0.055)] pointer-events-none"></div>
        
        <div className="relative z-10">
          <h2 className="font-serif text-[clamp(40px,6vw,84px)] font-black leading-[0.9] tracking-[-2px] mb-6">
            Never lose<br/>an insight <em className="italic text-[#E8A030]">again.</em>
          </h2>
          <p className="text-[16px] text-[#8C7A5A] max-w-[520px] mx-auto mb-10 leading-[1.8]">
            Ryan remembers what AI forgets. <strong className="text-[#FDF8EE] font-medium">Start free</strong> and build your personal AI encyclopedia today.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <button onClick={login} className="btn-primary" data-testid="cta-login-btn">Get Started Free →</button>
            <button onClick={() => scrollTo("features")} className="btn-ghost">Learn More</button>
          </div>
          <div className="mt-5 font-mono text-[9px] text-[#4A3A1A] tracking-[0.5px]">
            No spam. No credit card required.
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[rgba(232,160,48,0.1)] py-7 px-6 md:px-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 items-center gap-4">
          <div>
            <div className="font-serif font-black text-lg">Ryan</div>
            <div className="font-mono text-[8px] tracking-[2px] uppercase text-[#8C7A5A] mt-1">
              AI Conversation Memory
            </div>
          </div>
          <div className="text-center">
            <span className="font-serif text-lg text-[#E8A030] italic opacity-35">◈</span>
          </div>
          <div className="flex gap-6 md:justify-end">
            <a href="/privacy" className="font-mono text-[9px] tracking-[1.5px] uppercase text-[#D9C8A6] hover:text-[#E8A030] transition-colors" data-testid="footer-privacy-link">Privacy</a>
            <a href="/terms" className="font-mono text-[9px] tracking-[1.5px] uppercase text-[#D9C8A6] hover:text-[#E8A030] transition-colors" data-testid="footer-terms-link">Terms</a>
            <a href="mailto:ryan@ryanremembers.com" className="font-mono text-[9px] tracking-[1.5px] uppercase text-[#D9C8A6] hover:text-[#E8A030] transition-colors" data-testid="footer-contact-link">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
